import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useForm, useFormState } from 'react-final-form';
import { isEmpty } from 'lodash';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { addToast } from 'src/old/actions';
import { useConnectBizneoMutation, useSaveBizneoMutation } from 'src/shared/api/integrations/bizneo';
import { useGetIntegrationDataQuery } from 'src/shared/api/integrations/factorial';
import { toastTypes } from 'src/shared/components/Toast/ToastOptions';
import { getIsConnected } from '../utils/getIsConnected';
import { type BizneoCountry, type BizneoValues, mapFormDataToBizneoConfig } from './utils';

const integrationName = 'bizneo';

type BizneoCredentials = { apiKey?: string; baseUrl?: string };
type BizneoParameters = {
  country?: BizneoCountry;
  companyId?: number;
  includeAbsenceTypes?: number[];
};

export const useBizneoConnection = () => {
  const form = useForm();
  const [selectedCompany, setSelectedCompany] = useState('');
  const previousSelectedCompany = useRef('');
  const { data: integrationData, isLoading } = useGetIntegrationDataQuery();
  const [triggerConnection] = useConnectBizneoMutation();
  const { values } = useFormState();

  const isConnected = getIsConnected({
    isLoading,
    integrationData,
    subsidiary: values.subsidiary,
    integrationName
  });

  const company = integrationData?.find(
    item => item.subsidiary === selectedCompany && item.software === integrationName
  );

  const handleSelectedCompanyChange = (newCompany: string) => {
    setSelectedCompany(newCompany);
  };

  useEffect(() => {
    if (!integrationData || !selectedCompany) {
      return;
    }

    const companyChanged = previousSelectedCompany.current !== selectedCompany;
    previousSelectedCompany.current = selectedCompany;

    if (!companyChanged) {
      return;
    }

    if (!company) {
      form.change('apiKey', '');
      form.change('baseUrl', '');
      form.change('country', '');
      form.change('companyId', '');
      form.change('includeAbsenceTypes', []);
      form.change('withCustomFields', false);
      form.change('customFields', null);
      return;
    }

    const parameters = company.parameters as BizneoParameters | undefined;

    form.change('country', parameters?.country);
    form.change('companyId', parameters?.companyId != null ? String(parameters.companyId) : '');
    form.change(
      'includeAbsenceTypes',
      (parameters?.includeAbsenceTypes ?? []).map(id => String(id))
    );

    if (!isEmpty(company.fieldMap?.fieldMapping)) {
      form.change('withCustomFields', true);
      form.change('customFields', company.fieldMap?.fieldMapping);
    } else {
      form.change('withCustomFields', false);
      form.change('customFields', null);
    }
  }, [integrationData, form, selectedCompany, company]);

  const isConnectBtnDisabled = useMemo(() => {
    const allFilled = Boolean(values.apiKey && values.baseUrl);
    const credentials = company?.credentials as BizneoCredentials | undefined;
    const unchangedCredentials = values.apiKey === credentials?.apiKey && values.baseUrl === credentials?.baseUrl;

    return !allFilled || Boolean(isConnected && unchangedCredentials);
  }, [values.apiKey, values.baseUrl, company, isConnected]);

  return {
    selectedCompany,
    handleSelectedCompanyChange,
    triggerConnection,
    isConnected,
    isConnectBtnDisabled
  };
};

export const useBizneoSaveIntegration = () => {
  const [saveConfig] = useSaveBizneoMutation();
  const dispatch = useDispatch();
  const { t } = useTranslation('superAdmin');

  const saveBizneoIntegrationData = () => (formData: BizneoValues) => async () => {
    try {
      const payload = mapFormDataToBizneoConfig(formData);

      if (!payload) {
        dispatch(addToast({ ...toastTypes.error, defaultMessage: t('toasts.error.invalid data') }));
        return;
      }

      await saveConfig({ ...payload, subsidiary: formData.subsidiary }).unwrap();
      dispatch(addToast({ ...toastTypes.success, defaultMessage: t('toasts.updated') }));
    } catch (error) {
      console.error(error);
      const defaultMessage =
        (error as FetchBaseQueryError)?.status === 400 ? t('toasts.error.invalid data') : t('toasts.error');
      dispatch(addToast({ ...toastTypes.error, defaultMessage }));
    }
  };

  return saveBizneoIntegrationData;
};
